import { reactive, computed, inject } from 'vue';
import { getCategoryIconFallbackForSlug, resolveCategoryIconFromApi, } from '@shared/config/categoryIconRegistry';
import { readPrerenderMenuBundleFromDocument } from '@shared/utils/prerenderMenuHydration';
import { mergeCategoryArraysInPlace, mergeMenuItemArraysInPlace, mergeObjectArraysInPlace, menuBundlesEquivalent, resolveMenuEntityId } from '@shared/utils/menuStateMerge';
/**
 * Enhanced Composable for Menu Management
 * Shared across all restaurant projects with extended functionality
 */
export function useMenu() {
    const apiClient = inject('apiClient');
    if (!apiClient) {
        throw new Error('RestaurantApiClient not provided. Make sure to provide it in your app.');
    }
    const normalizeComboMenus = (raw = []) => raw
        .filter((m) => m && m.isActive !== false)
        .map((m) => ({
        id: m._id || m.id,
        _id: m._id || m.id,
        name: m.name,
        description: m.description,
        menuPriceCent: m.menuPriceCent,
        referenceSavingsCent: m.referenceSavingsCent,
        imageUrl: m.imageUrl || m.image,
        components: m.components || []
    }));
    const state = reactive({
        items: [],
        categories: [],
        comboMenus: [],
        ingredients: [],
        ingredientCategories: [],
        loading: false,
        error: null
    });
    const menuCategories = computed(() => {
        const categories = new Set();
        state.items.forEach(item => {
            const categoryName = typeof item.category === 'object' ? item.category.name : item.category;
            categories.add(categoryName);
        });
        return Array.from(categories);
    });
    const groupedCategories = computed(() => {
        return groupProductsByCategory(state.items);
    });
    const getItemsByCategory = (category) => {
        return computed(() => state.items.filter(item => {
            const categoryName = typeof item.category === 'object' ? item.category.name : item.category;
            return categoryName === category;
        }));
    };
    const getItemById = (id) => {
        return computed(() => state.items.find(item => item.id === id));
    };
    const buildMenuStateFromBundle = (bundleData) => {
        if (!bundleData?.products)
            return null;
        const items = bundleData.products.map((product) => {
            if (product.sizes && Array.isArray(product.sizes)) {
                product.sizes = product.sizes.map((size) => ({
                    ...size,
                    id: size._id || size.id,
                    price: (size.priceModifierCent || size.priceCent || 0) / 100,
                    priceCent: size.priceModifierCent || size.priceCent || 0,
                    priceModifierCent: size.priceModifierCent || size.priceCent || 0
                }));
            }
            return product;
        });
        let categories = [];
        if (bundleData.categories && bundleData.categories.length > 0) {
            const productsByCategory = new Map();
            const resolveProductCategoryKey = (product) => {
                const category = product?.category;
                if (typeof category === 'string')
                    return category;
                if (category && typeof category === 'object') {
                    return category._id || category.id || category.slug || category.name || null;
                }
                return product?.categoryId || null;
            };
            items.forEach((product) => {
                const categoryKey = resolveProductCategoryKey(product);
                if (!categoryKey)
                    return;
                if (!productsByCategory.has(categoryKey)) {
                    productsByCategory.set(categoryKey, []);
                }
                productsByCategory.get(categoryKey).push(product);
            });
            productsByCategory.forEach((products) => {
                products.sort((a, b) => {
                    const sortA = a.sortOrder || 0;
                    const sortB = b.sortOrder || 0;
                    if (sortA !== sortB)
                        return sortA - sortB;
                    return (a.name || '').localeCompare(b.name || '');
                });
            });
            const sortedCategories = [...bundleData.categories].sort((a, b) => {
                const sortA = a.sortOrder || 0;
                const sortB = b.sortOrder || 0;
                if (sortA !== sortB)
                    return sortA - sortB;
                return (a.name || '').localeCompare(b.name || '');
            });
            const categoriesFromApi = sortedCategories
                .map((apiCategory) => {
                const categoryProducts = productsByCategory.get(apiCategory._id) ||
                    productsByCategory.get(apiCategory.id) ||
                    productsByCategory.get(apiCategory.slug) ||
                    productsByCategory.get(apiCategory.name) ||
                    [];
                return {
                    id: apiCategory.slug || apiCategory._id,
                    name: apiCategory.name,
                    description: apiCategory.description,
                    icon: resolveCategoryIconFromApi(apiCategory.icon, apiCategory.slug || apiCategory._id || apiCategory.name),
                    color: apiCategory.color,
                    items: categoryProducts
                };
            })
                .filter((cat) => Array.isArray(cat.items) && cat.items.length > 0);
            categories = categoriesFromApi.length > 0 || items.length === 0
                ? categoriesFromApi
                : groupProductsByCategory(items, bundleData.categories);
        }
        else {
            categories = groupProductsByCategory(items, bundleData.categories);
        }
        return {
            items,
            categories,
            ingredients: bundleData.ingredients || [],
            ingredientCategories: bundleData.ingredientCategories || [],
            comboMenus: normalizeComboMenus(bundleData.menus || [])
        };
    };
    const applyMenuBundleData = (bundleData, options) => {
        const built = buildMenuStateFromBundle(bundleData);
        if (!built)
            return false;
        const shouldMerge = options?.merge === true && state.items.length > 0;
        if (shouldMerge) {
            const currentSnapshot = {
                products: state.items,
                categories: state.categories,
                ingredients: state.ingredients,
                ingredientCategories: state.ingredientCategories,
                menus: state.comboMenus
            };
            const incomingSnapshot = {
                products: built.items,
                categories: built.categories,
                ingredients: built.ingredients,
                ingredientCategories: built.ingredientCategories,
                menus: built.comboMenus
            };
            if (menuBundlesEquivalent(currentSnapshot, incomingSnapshot)) {
                state.error = null;
                state.loading = false;
                return state.items.length > 0;
            }
            mergeMenuItemArraysInPlace(state.items, built.items);
            mergeCategoryArraysInPlace(state.categories, built.categories);
            mergeObjectArraysInPlace(state.ingredients, built.ingredients, (item) => resolveMenuEntityId(item));
            mergeObjectArraysInPlace(state.ingredientCategories, built.ingredientCategories, (item) => resolveMenuEntityId(item));
            const comboById = new Map(state.comboMenus.map((menu) => [resolveMenuEntityId(menu), menu]));
            for (const combo of built.comboMenus) {
                const id = resolveMenuEntityId(combo);
                const current = comboById.get(id);
                if (current) {
                    Object.assign(current, combo);
                }
                else {
                    state.comboMenus.push(combo);
                }
            }
            const nextComboIds = new Set(built.comboMenus.map((menu) => resolveMenuEntityId(menu)));
            for (let index = state.comboMenus.length - 1; index >= 0; index -= 1) {
                if (!nextComboIds.has(resolveMenuEntityId(state.comboMenus[index]))) {
                    state.comboMenus.splice(index, 1);
                }
            }
        }
        else {
            state.items = built.items;
            state.categories = built.categories;
            state.ingredients = built.ingredients;
            state.ingredientCategories = built.ingredientCategories;
            state.comboMenus = built.comboMenus;
        }
        state.error = null;
        state.loading = false;
        return state.items.length > 0;
    };
    let prerenderMenuHydrated = false;
    const hydrateFromPrerenderDocument = () => {
        if (prerenderMenuHydrated || state.items.length > 0) {
            return state.items.length > 0;
        }
        const bundle = readPrerenderMenuBundleFromDocument();
        if (!bundle)
            return false;
        const ok = applyMenuBundleData(bundle);
        if (ok)
            prerenderMenuHydrated = true;
        return ok;
    };
    const loadMenu = async () => {
        state.loading = true;
        state.error = null;
        try {
            console.log('🍽️ Loading menu from shared API client...');
            // ✅ Use getMenuBundle() instead of getMenu() to get full bundle with categories
            const response = await apiClient.getMenuBundle();
            if (response.status === 'success' && response.data) {
                applyMenuBundleData(response.data);
                return state.items;
            }
            else {
                state.error = response.message || 'Failed to load menu';
                console.error('❌ Failed to load menu:', state.error);
                return [];
            }
        }
        catch (error) {
            state.error = error.message || 'Unknown error occurred';
            console.error('❌ Menu loading error:', error);
            return [];
        }
        finally {
            state.loading = false;
        }
    };
    const loadMenuItem = async (id) => {
        try {
            console.log(`🍽️ Loading menu item ${id}...`);
            const response = await apiClient.getMenuItem(id);
            if (response.status === 'success' && response.data) {
                return response.data;
            }
            else {
                console.error(`❌ Menu item ${id} not found:`, response.message);
                return null;
            }
        }
        catch (error) {
            console.error('❌ Error loading menu item:', error);
            return null;
        }
    };
    /**
     * Get special deals/featured items
     */
    const loadSpecialDeals = async (limit = 8) => {
        try {
            console.log('🔥 Loading special deals...');
            const response = await apiClient.getSpecialDeals(limit);
            if (response.status === 'success' && response.data) {
                return response.data;
            }
            else {
                console.error('❌ Failed to load special deals:', response.message);
                return [];
            }
        }
        catch (error) {
            console.error('❌ Special deals loading error:', error);
            return [];
        }
    };
    /**
     * Get full menu bundle (products, categories, ingredients)
     */
    const loadMenuBundle = async (options) => {
        if (!options?.forceNetwork && hydrateFromPrerenderDocument()) {
            return {
                products: state.items,
                categories: state.categories,
                ingredients: state.ingredients,
                ingredientCategories: state.ingredientCategories,
                menus: state.comboMenus
            };
        }
        try {
            const response = await apiClient.getMenuBundle();
            if (response.status === 'success' && response.data) {
                applyMenuBundleData(response.data, {
                    merge: options?.merge ?? state.items.length > 0
                });
                return response.data;
            }
            else {
                console.error('❌ Failed to load menu bundle:', response.message);
                return null;
            }
        }
        catch (error) {
            console.error('❌ Menu bundle loading error:', error);
            return null;
        }
    };
    return {
        // State
        menuItems: computed(() => state.items),
        comboMenus: computed(() => state.comboMenus),
        categories: computed(() => state.categories),
        ingredients: computed(() => state.ingredients),
        ingredientCategories: computed(() => state.ingredientCategories),
        menuCategories,
        groupedCategories,
        loading: computed(() => state.loading),
        error: computed(() => state.error),
        // Methods
        loadMenu,
        loadMenuItem,
        loadSpecialDeals,
        loadMenuBundle,
        hydrateFromPrerenderDocument,
        getItemsByCategory,
        getItemById,
        // Utils
        isLoading: computed(() => state.loading),
        hasError: computed(() => !!state.error),
        isEmpty: computed(() => state.items.length === 0)
    };
}
/**
 * Utility function to transform products to categories
 * Gruppiert Produkte nach Kategorien für die MenuPage
 */
export function groupProductsByCategory(products, categories) {
    console.log('🔄 [useMenu] groupProductsByCategory called with:', { productCount: products.length, categoriesProvided: !!categories });
    // Create category lookup map if categories array provided
    const categoryLookup = new Map();
    if (categories && categories.length > 0) {
        console.log('📋 [useMenu] Processing categories array:', categories.map((cat) => `${cat.name} (sortOrder: ${cat.sortOrder})`).join(', '));
        categories.forEach(cat => {
            const categoryId = cat._id || cat.id || cat.name;
            if (categoryId) {
                categoryLookup.set(categoryId, cat);
                // Also index by name/slug for fallback matching
                if (cat.name)
                    categoryLookup.set(cat.name, cat);
                if (cat.slug)
                    categoryLookup.set(cat.slug, cat);
            }
        });
    }
    // Temporäres Map für Kategorien
    const categoryMap = new Map();
    products.forEach(product => {
        // Kategorie kann jetzt ein Objekt sein (populated) oder ein String (fallback)
        const isPopulatedCategory = typeof product.category === 'object' && product.category !== null;
        let categoryId;
        let categoryData = null;
        let categoryName;
        let categorySlug;
        let categoryDescription;
        let categoryIcon = null;
        let categoryColor = null;
        // 1. Try to get category ID first
        if (isPopulatedCategory) {
            const popCat = product.category;
            categoryId = popCat._id || popCat.id || popCat.name || 'unknown';
            categoryData = popCat;
        }
        else if (typeof product.category === 'string') {
            categoryId = product.category;
        }
        else {
            categoryId = 'unknown';
        }
        // 2. Look up full category data from categories array
        if (!categoryData && categoryLookup.has(categoryId)) {
            categoryData = categoryLookup.get(categoryId);
        }
        // 3. If still no category data, try fallback lookups
        if (!categoryData && product.categoryName) {
            const fallbackName = product.categoryName;
            if (categoryLookup.has(fallbackName)) {
                categoryData = categoryLookup.get(fallbackName);
            }
        }
        // 4. Extract category information (prioritize full category data)
        if (categoryData) {
            // ✅ Use full category data
            categoryName = categoryData.name || categoryData.categoryName || categoryId;
            categorySlug = categoryData.slug || categoryName.toLowerCase().replace(/\s+/g, '-');
            categoryDescription = categoryData.description || `Unsere ${categoryName.toLowerCase()} Auswahl`;
            categoryIcon = resolveCategoryIconFromApi(categoryData.icon, categoryData.slug || categorySlug || categoryId);
            categoryColor = categoryData.color || null;
        }
        else if (product.categoryName) {
            // ✅ Fallback to product's category fields
            categoryName = product.categoryName;
            categorySlug = product.categorySlug || categoryName.toLowerCase().replace(/\s+/g, '-');
            categoryDescription = `Unsere ${categoryName.toLowerCase()} Auswahl`;
            categoryIcon = null;
        }
        else if (isPopulatedCategory) {
            // ✅ Fallback to populated category object
            const popCat = product.category;
            categoryName = popCat.name || popCat.categoryName || categoryId;
            categorySlug = popCat.slug || categoryName.toLowerCase().replace(/\s+/g, '-');
            categoryDescription = popCat.description || `Unsere ${categoryName.toLowerCase()} Auswahl`;
            categoryIcon = resolveCategoryIconFromApi(popCat.icon, popCat.slug || categorySlug || categoryId);
            categoryColor = popCat.color || null;
        }
        else {
            // ✅ Final fallback to category ID
            categoryName = categoryId;
            categorySlug = categoryId.toLowerCase().replace(/\s+/g, '-');
            categoryDescription = `Unsere ${categoryName.toLowerCase()} Auswahl`;
            categoryIcon = null;
        }
        const mapKey = categorySlug;
        if (!categoryMap.has(mapKey)) {
            // Neue Kategorie erstellen mit vollständigen Informationen
            categoryMap.set(mapKey, {
                id: categoryId, // ✅ Use original MongoDB ID, not slug
                name: categoryName,
                description: categoryDescription,
                icon: categoryIcon || getCategoryIconFallbackForSlug(categorySlug || mapKey),
                color: categoryColor || categoryData?.color || undefined,
                items: [],
                sortOrder: categoryData?.sortOrder || 0 // ✅ Store sortOrder for sorting
            });
        }
        // Produkt zur Kategorie hinzufügen
        categoryMap.get(mapKey).items.push(product);
    });
    // ✅ Sort categories by sortOrder and then by name
    const sortedCategories = Array.from(categoryMap.values()).sort((a, b) => {
        // Use stored sortOrder from category data
        const sortA = a.sortOrder || 0;
        const sortB = b.sortOrder || 0;
        console.log(`🔢 [useMenu] Comparing categories: ${a.name} (sortOrder: ${sortA}) vs ${b.name} (sortOrder: ${sortB})`);
        if (sortA !== sortB) {
            return sortA - sortB;
        }
        // Fallback to name sorting
        return a.name.localeCompare(b.name);
    });
    // ✅ Sort products within each category by sortOrder
    sortedCategories.forEach(category => {
        category.items.sort((a, b) => {
            const sortA = a.sortOrder || 0;
            const sortB = b.sortOrder || 0;
            if (sortA !== sortB) {
                return sortA - sortB;
            }
            // Fallback to name sorting if sortOrder is the same
            return (a.name || '').localeCompare(b.name || '');
        });
    });
    console.log('📋 [useMenu] Final category order:', sortedCategories.map(cat => `${cat.name} (sortOrder: ${cat.sortOrder})`).join(', '));
    // ✅ Remove sortOrder from final output (clean Category interface)
    return sortedCategories.map(category => ({
        id: category.id,
        name: category.name,
        description: category.description,
        icon: category.icon,
        color: category.color,
        items: category.items
    }));
}
